// src/modules/listaagendacolaborador/index.jsx

import React, { useState, useEffect, useCallback } from "react";
import env from "../../config/env.js";
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, TextField, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Alert,
  Snackbar, TablePagination, TableSortLabel
} from "@mui/material";
import { ptBR } from "date-fns/locale";
import { format, startOfWeek, endOfWeek, addDays } from "date-fns";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from "../../services/api";
import AcessoRestrito from '../../components/AcessoRestrito';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Print as PrintIcon
} from "@mui/icons-material";


export default function ListaAgendaColaborador() {
  const [agendamentos, setAgendamentos] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedColaborador, setSelectedColaborador] = useState('');
  const [diasSemana, setDiasSemana] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("horario");
  const [order, setOrder] = useState("asc");

  // Buscar colaboradores, clientes e serviços
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Buscar colaboradores
        const resColaboradores = await api.get('/colaboradores');
        setColaboradores(Array.isArray(resColaboradores.data) ? resColaboradores.data : []);

        // Buscar clientes
        const resClientes = await api.get('/clientes');
        setClientes(Array.isArray(resClientes.data) ? resClientes.data : []);

        // Buscar serviços
        const resServicos = await api.get('/servicos');
        setServicos(Array.isArray(resServicos.data.dados) ? resServicos.data.dados : []);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };

    fetchData();
  }, []);

  // Atualizar dias da semana quando a data selecionada muda
  useEffect(() => {
    const inicioSemana = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const dias = [];
    for (let i = 0; i < 7; i++) {
      dias.push(addDays(inicioSemana, i));
    }
    setDiasSemana(dias);
  }, [selectedDate]);

  // Função para obter agendamentos do dia
  const getAgendamentosDoDia = (data) => {
    return agendamentos.filter(agendamento => {
      const dataAgendamento = new Date(agendamento.dataHoraInicio);
      return format(dataAgendamento, 'yyyy-MM-dd') === format(data, 'yyyy-MM-dd');
    });
  };

  // Buscar agendamentos
  const buscarAgendamentos = async () => {
    if (!selectedColaborador) return;

    setLoading(true);
    try {
      const dataInicio = format(startOfWeek(selectedDate, { weekStartsOn: 1 }), "yyyy-MM-dd'T'00:00:00'Z'");
      const dataFim = format(endOfWeek(selectedDate, { weekStartsOn: 1 }), "yyyy-MM-dd'T'00:00:00'Z'");

      const res = await api.get(`/agendamentos/colaborador/${selectedColaborador}?dataInicio=${dataInicio}&dataFim=${dataFim}`);
      setAgendamentos(res.data);
    } catch (err) {
      console.error('Erro ao buscar agendamentos:', err);
      setError('Erro ao carregar agendamentos');
    } finally {
      setLoading(false);
    }
  };

  // Buscar agendamentos quando mudar colaborador ou data
  useEffect(() => {
    if (selectedColaborador) {
      buscarAgendamentos();
    }
  }, [selectedColaborador, selectedDate]);

  const handleRequestSort = (property) => {
    const isAsc = order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatarDataHora = useCallback((data) => {
    return format(data, "dd/MM/yyyy HH:mm", { locale: ptBR });
  }, []);

  // Função para imprimir a agenda
  const handleImprimir = async () => {
    if (!selectedColaborador) {
      setError('Selecione um colaborador para imprimir a agenda');
      return;
    }

    try {
      // Buscar dados do colaborador
      const resColaborador = await api.get(`/colaboradores/${selectedColaborador}`);
      const nomeColaborador = resColaborador.data?.nome_completo || 'Colaborador';

      const dataInicio = format(startOfWeek(selectedDate, { weekStartsOn: 1 }), "dd/MM/yyyy", { locale: ptBR });
      const dataFim = format(endOfWeek(selectedDate, { weekStartsOn: 1 }), "dd/MM/yyyy", { locale: ptBR });

    // Criar conteúdo para impressão
    const conteudoImpressao = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Agenda do Colaborador - ${nomeColaborador}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
          .info { margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
          .agendamento { background-color: #4caf50; color: white; padding: 4px; margin: 2px 0; border-radius: 3px; font-size: 12px; }
          .horario { font-weight: bold; background-color: #f5f5f5; }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Agenda do Colaborador</h1>
          <h2>${nomeColaborador}</h2>
          <p>Período: ${dataInicio} a ${dataFim}</p>
        </div>
        
        <table>
          <thead>
            <tr>
              <th width="15%">Horário</th>
              ${diasSemana.map(dia => `<th width="12%">${format(dia, 'EEEE, dd/MM', { locale: ptBR })}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${Array.from({ length: 11 }, (_, i) => i + 8).map(hora => `
              <tr>
                <td class="horario">${hora.toString().padStart(2, '0')}:00</td>
                ${diasSemana.map(dia => {
                  const agendamentosDoDia = getAgendamentosDoDia(dia);
                  const agendamentosDaHora = agendamentosDoDia.filter(a => {
                    const horaAgendamento = new Date(a.dataHoraInicio).getHours();
                    return horaAgendamento === hora;
                  });
                  
                  if (agendamentosDaHora.length === 0) {
                    return '<td></td>';
                  }
                  
                  const agendamentosHtml = agendamentosDaHora.map(agendamento => {
                    const cliente = clientes.find(c => c.id === agendamento.clienteId);
                    const servico = servicos.find(s => s.id === agendamento.servicoId);
                    return `
                      <div class="agendamento">
                        <strong>${format(new Date(agendamento.dataHoraInicio), 'HH:mm')} - ${format(new Date(agendamento.dataHoraFim), 'HH:mm')}</strong><br>
                        Cliente: ${cliente?.nome_completo || agendamento.clienteId}<br>
                        Serviço: ${servico?.nome || 'Serviço não encontrado'}
                      </div>
                    `;
                  }).join('');
                  
                  return `<td>${agendamentosHtml}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div style="margin-top: 30px; text-align: center; font-size: 12px; color: #666;">
          <p>Documento gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
        </div>
      </body>
      </html>
    `;

    // Abrir janela de impressão
    const janelaImpressao = window.open('', '_blank');
    janelaImpressao.document.write(conteudoImpressao);
    janelaImpressao.document.close();
    janelaImpressao.focus();
    
    // Aguardar um pouco para o conteúdo carregar antes de imprimir
    setTimeout(() => {
      janelaImpressao.print();
      janelaImpressao.close();
    }, 500);
    } catch (error) {
      console.error('Erro ao imprimir agenda:', error);
      setError('Erro ao gerar impressão da agenda');
    }
  };

  return (
    <AcessoRestrito modulo="listaagendacolaborador">
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          height: '80vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {error && (
        <Snackbar 
          open={!!error} 
          autoHideDuration={env.NOTIFICATION_DURATION || 6000} 
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
        )}

        {/* Cabeçalho */}
        <Box sx={{ p: 3, pb: 2 }} display="flex" justifyContent="space-between" flexWrap="wrap">
          <Typography variant="h5" mb={1}>Agenda do Colaborador</Typography>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handleImprimir}
            disabled={!selectedColaborador || loading}
          >
            Imprimir Agenda
          </Button>
        </Box>

        {/* Filtros */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Colaborador</InputLabel>
              <Select
                value={selectedColaborador}
                onChange={(e) => setSelectedColaborador(e.target.value)}
                label="Colaborador"
              >
                {colaboradores.map(colaborador => (
                  <MenuItem key={colaborador.id} value={colaborador.id}>
                    {colaborador.nome_completo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label="Semana"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        {/* Container da Tabela */}
        <TableContainer 
          component={Paper} 
          sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'auto',
            height: '100%',
            maxHeight: '100%',
            mx: 3,
            width: 'calc(100% - 48px)',
            mb: 9
          }}
        >
          <Table stickyHeader sx={{ minWidth: 650, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell width="15%">
                  <TableSortLabel
                    active={orderBy === "horario"}
                    direction={orderBy === "horario" ? order : "asc"}
                    onClick={() => handleRequestSort("horario")}
                  >
                    Horário
                  </TableSortLabel>
                </TableCell>
                {diasSemana.map((dia) => (
                  <TableCell key={format(dia, 'yyyy-MM-dd')} width="12%">
                    {format(dia, 'EEEE, dd/MM', { locale: ptBR })}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : !selectedColaborador ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="h6" color="text.secondary">
                      Selecione um colaborador para visualizar a agenda
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                // Horários das 8h às 18h
                Array.from({ length: 11 }, (_, i) => i + 8).map((hora) => (
                  <TableRow key={hora}>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {`${hora.toString().padStart(2, '0')}:00`}
                    </TableCell>
                    {diasSemana.map((dia) => {
                      const agendamentosDoDia = getAgendamentosDoDia(dia);
                      
                      const agendamentosDaHora = agendamentosDoDia.filter(a => {
                        const horaAgendamento = new Date(a.dataHoraInicio).getHours();
                        return horaAgendamento === hora;
                      });

                      return (
                        <TableCell key={format(dia, 'yyyy-MM-dd')} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {agendamentosDaHora.map((agendamento, index) => (
                            <Box 
                              key={agendamento._id}
                              sx={{ 
                                p: 1, 
                                bgcolor: '#4caf50', 
                                color: 'white',
                                borderRadius: 1,
                                mb: index < agendamentosDaHora.length - 1 ? 1 : 0,
                                fontSize: '0.75rem'
                              }}
                            >
                              <Typography variant="body2" noWrap sx={{ fontSize: 'inherit' }}>
                                {format(new Date(agendamento.dataHoraInicio), 'HH:mm')} - {format(new Date(agendamento.dataHoraFim), 'HH:mm')}
                              </Typography>
                              <Typography variant="body2" noWrap sx={{ fontSize: 'inherit' }}>
                                Cliente: {Array.isArray(clientes) && clientes.find(c => c.id === agendamento.clienteId)?.nome_completo || agendamento.clienteId}
                              </Typography>
                              <Typography variant="body2" noWrap sx={{ fontSize: 'inherit' }}>
                                Serviço: {Array.isArray(servicos) && servicos.find(s => s.id === agendamento.servicoId)?.nome || 'Serviço não encontrado'}
                              </Typography>
                            </Box>
                          ))}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação Fixa */}
        <TablePagination
          component="div"
          count={selectedColaborador ? 11 : 0} // 11 horários (8h às 18h)
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Itens por página"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            width: '100%',
            px: 3,
            py: 1.5,
            boxSizing: 'border-box',
            zIndex: 1000,
            minHeight: '60px'
          }}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />

        {/* Resumo Fixo */}
        <Box sx={{ 
          position: 'fixed',
          bottom: '60px',
          left: 0,
          right: 0,
          p: 2,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 999,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6">
            {selectedColaborador ? `Agenda de ${colaboradores.find(c => c.id === selectedColaborador)?.nome_completo || 'Colaborador'}` : 'Selecione um colaborador'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedColaborador ? `${agendamentos.length} agendamento(s) encontrado(s)` : 'Nenhum colaborador selecionado'}
          </Typography>
        </Box>
      </Box>
    </AcessoRestrito>
  );
}